import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, AreaChart, Area, ComposedChart } from 'recharts';
import apiService from '../../services/apiService';
import TrendIndicator from './TrendIndicator';

const COLORS = ['#818cf8', '#34d399', '#fbbf24', '#f472b6', '#38bdf8', '#fb923c'];

const formatCurrency = (v) => `£${v.toLocaleString()}`;

const ChartTooltip = ({ active, payload, label, isCurrency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-3 py-2 text-sm">
      <p className="text-slate-500">{label}</p>
      <p className="text-slate-800 font-medium">{isCurrency ? formatCurrency(payload[0].value) : payload[0].value}</p>
    </div>
  );
};

const AnalyticsTab = ({ title }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);

  const fetchData = useCallback((year) => {
    setLoading(true);
    apiService.getDashboardAnalytics(year)
      .then(res => {
        setData(res);
        if (res.availableYears?.length) setAvailableYears(res.availableYears);
        if (!selectedYear && res.year) setSelectedYear(res.year);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [selectedYear]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleYearChange = (year) => {
    setSelectedYear(year);
    fetchData(year);
  };

  if (loading && !data) return (
    <div className="text-center py-20">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-slate-500">Loading analytics...</p>
    </div>
  );

  if (!data) return (
    <div className="text-center py-20"><p className="text-slate-500">Unable to load analytics data</p></div>
  );

  const displayYear = selectedYear || new Date().getFullYear();

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-slate-800">{title || 'Payment Trends'}</h2>
        {availableYears.length > 1 && (
          <div className="flex items-center gap-2">
            <span className="text-slate-500 text-sm">Year:</span>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
              {availableYears.slice(0, 3).map(y => (
                <button
                  key={y}
                  onClick={() => handleYearChange(y)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                    y === displayYear
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {y}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {loading && <div className="text-center text-slate-400 text-sm mb-4">Updating...</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Payments Bar Chart */}
        <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
          <h3 className="text-slate-600 text-sm font-semibold mb-3">Monthly Payments ({displayYear})</h3>
          {data.monthlyPayments?.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.monthlyPayments}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip content={<ChartTooltip isCurrency />} />
                  <Bar dataKey="total" fill="#818cf8" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-slate-400 text-sm py-10 text-center">No payment data for {displayYear}</p>}
        </div>

        {/* Membership Growth */}
        <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
          <h3 className="text-slate-600 text-sm font-semibold mb-3">Membership Overview ({displayYear})</h3>
          {data.membershipGrowth?.length > 0 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.membershipGrowth}>
                  <defs>
                    <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#818cf8" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#818cf8" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis yAxisId="total" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <YAxis yAxisId="new" orientation="right" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-3 py-2 text-sm">
                        <p className="text-slate-500 mb-1">{label}</p>
                        {payload.map((p, i) => (
                          <p key={i} style={{ color: p.color }} className="font-medium">
                            {p.name === 'totalMembers' ? 'Total' : 'New'}: {p.value}
                          </p>
                        ))}
                      </div>
                    );
                  }} />
                  <Area yAxisId="total" type="monotone" dataKey="totalMembers" stroke="#818cf8" fill="url(#totalGradient)" strokeWidth={2} name="totalMembers" />
                  <Bar yAxisId="new" dataKey="newMembers" fill="#34d399" radius={[3, 3, 0, 0]} barSize={16} name="newMembers" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-slate-400 text-sm py-10 text-center">No growth data for {displayYear}</p>}
        </div>

        {/* Collection Rate */}
        <div className="p-5 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-center">
          <div>
            <h3 className="text-slate-600 text-sm font-semibold mb-4 text-center">
              Collection Rate {displayYear !== new Date().getFullYear() ? `(${displayYear} avg)` : ''}
            </h3>
            <TrendIndicator current={data.collectionRate?.current || 0} previous={data.collectionRate?.previous || 0} />
          </div>
        </div>

        {/* Payment Methods Donut */}
        <div className="p-5 rounded-xl bg-slate-50 border border-slate-100">
          <h3 className="text-slate-600 text-sm font-semibold mb-3">Payment Methods ({displayYear})</h3>
          {data.paymentMethods?.length > 0 ? (
            <>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.paymentMethods} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="count" nameKey="method" paddingAngle={2}>
                      {data.paymentMethods.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-3 justify-center mt-2">
                {data.paymentMethods.map((d, i) => (
                  <div key={d.method} className="flex items-center gap-1.5 text-xs">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                    <span className="text-slate-500">{d.method} ({d.count})</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-slate-400 text-sm py-10 text-center">No payment method data for {displayYear}</p>}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
